'use client';

import { FC, useMemo, useState } from 'react';
import XIcon from './x_icon';
import CaseStudyCard from '@/components/CaseStudyCard/CaseStudyCard';
import { ChevronDown } from 'lucide-react';
import { TestimonialCarousel2SliceWithFiltersPrimaryCardsItem } from '../../../../prismicio-types';
import { KeyTextField } from '@prismicio/client';

type FilterOptionType = 'Industry' | 'Country' | 'Service' | 'Technology';

interface WithFiltersProps {
  loadMoreText: KeyTextField;
  clearFiltersText: KeyTextField;
  caseStudies: Array<TestimonialCarousel2SliceWithFiltersPrimaryCardsItem>;
  slice: {
    slice_type: string;
    variation: string;
  };
}

const WithFilters: FC<WithFiltersProps> = (props) => {
  const { slice } = props;

  const { loadMoreText, clearFiltersText, caseStudies: rawCaseStudies } = props;

  const caseStudies = useMemo(() => {
    return rawCaseStudies.map((cs) => ({
      client: cs.client || '',
      link: cs.link,
      title: cs.industry || '',
      description: cs.title || '',
      industry: cs.industry || '',
      country: cs.country || '',
      service: cs.service || '',
      technology: cs.technology || '',
      backgroundImage: cs.image || null,
    }));
  }, [rawCaseStudies]);

  const filterOptions: FilterOptionType[] = [
    'Industry',
    'Country',
    'Service',
    'Technology',
  ];

  const [selectedFilters, setSelectedFilters] = useState<{
    [key in FilterOptionType]: string;
  }>({
    Industry: '',
    Country: '',
    Service: '',
    Technology: '',
  });
  const [numberOfCases, setNumberOfCases] = useState(8);

  const addMoreCases = () => {
    setNumberOfCases((prev) => prev + 3);
  };

  const filteredCaseStudies = caseStudies.filter((caseStudy) => {
    return (
      (selectedFilters.Industry
        ? caseStudy.industry.toLowerCase() ===
          selectedFilters.Industry.toLowerCase()
        : true) &&
      (selectedFilters.Country
        ? caseStudy.country.toLowerCase() ===
          selectedFilters.Country.toLowerCase()
        : true) &&
      (selectedFilters.Service
        ? caseStudy.service.toLowerCase() ===
          selectedFilters.Service.toLowerCase()
        : true) &&
      (selectedFilters.Technology
        ? caseStudy.technology.toLowerCase() ===
          selectedFilters.Technology.toLowerCase()
        : true)
    );
  });

  const slicedFilteredCaseStudies = useMemo(
    () => filteredCaseStudies.slice(0, numberOfCases),
    [filteredCaseStudies, numberOfCases],
  );

  const industriesOptions = useMemo(
    () => Array.from(new Set(caseStudies.map((cs) => cs.industry))),
    [caseStudies],
  );
  const countriesOptions = useMemo(
    () => Array.from(new Set(caseStudies.map((cs) => cs.country))),
    [caseStudies],
  );
  const servicesOptions = useMemo(
    () => Array.from(new Set(caseStudies.map((cs) => cs.service))),
    [caseStudies],
  );
  const technologiesOptions = useMemo(
    () => Array.from(new Set(caseStudies.map((cs) => cs.technology))),
    [caseStudies],
  );

  const optionsByFilter = {
    Industry: industriesOptions,
    Country: countriesOptions,
    Service: servicesOptions,
    Technology: technologiesOptions,
  };

  const handleSelectOptionChange = (
    optionType: FilterOptionType,
    value: string,
  ) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [optionType]: value,
    }));
  };

  const renderFilterOptions = () => {
    return filterOptions.map((option) => (
      <div
        key={option}
        className="relative w-full flex flex-row items-center gap-2 text-white border border-white hover:bg-[#FFFFFF80] rounded-lg px-3 py-2"
      >
        <select
          name={option.toLowerCase()}
          id={option.toLowerCase()}
          className="ds-select relative z-20 bg-transparent appearance-none outline-none pr-4 w-full"
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleSelectOptionChange(option, e.target.value)
          }
          value={selectedFilters[option]}
        >
          <option value="">{option}</option>
          {optionsByFilter[option].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute bottom-2 right-1 z-10" />
      </div>
    ));
  };

  const renderFiltersSelected = () => {
    return (
      <div className="flex flex-row gap-3">
        {Object.values(selectedFilters).map((filter, index) => {
          if (filter) {
            return (
              <div
                key={`${filter}-${index}`}
                className="flex flex-row px-4 py-1 gap-[10px] rounded-[41px] border border-[#05F273] text-[#05F273]"
              >
                <span>{filter}</span>
                <span
                  className="cursor-pointer"
                  onClick={() => {
                    const newFilters = { ...selectedFilters };
                    const filterKey = Object.keys(selectedFilters)[index];
                    newFilters[filterKey as FilterOptionType] = '';
                    setSelectedFilters(newFilters);
                  }}
                >
                  <XIcon />
                </span>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-gradient-to-b from-[#0F423A] to-[#0E1010] pb-48"
    >
      <div className="container mx-auto px-5 md:px-10 flex flex-col gap-[60px]">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 flex-1">
            {renderFilterOptions()}
          </div>
          <button
            className="w-full md:w-[200px] px-6 py-3 rounded-[100px] border-t border-[#EAEAEA] bg-[#EAEAEA4D] hover:bg-[#EAEAEA80] text-white text-[16px] font-[700] leading-6"
            onClick={() =>
              setSelectedFilters({
                Industry: '',
                Country: '',
                Service: '',
                Technology: '',
              })
            }
          >
            {clearFiltersText}
          </button>
        </div>
        {renderFiltersSelected()}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {slicedFilteredCaseStudies.map((caseStudy) => (
            <CaseStudyCard
              key={caseStudy.client}
              link={caseStudy.link}
              industry={caseStudy.industry}
              description={caseStudy.description}
              service={caseStudy.service}
              backgroundImage={caseStudy.backgroundImage}
            />
          ))}
        </div>
        <div className="flex flex-row justify-center">
          {slicedFilteredCaseStudies.length < filteredCaseStudies.length && (
            <button
              onClick={addMoreCases}
              className="px-6 py-3 rounded-[100px] bg-[#BEBEBE4D] hover:bg-[#BEBEBE80] border-t border-[#EAEAEA] text-white"
            >
              {loadMoreText}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default WithFilters;
